import React from 'react';
import { formatReportValue } from '../../utils/reportFormat';
import { resolveBarDisplay } from '../../utils/tableColumnDisplay';
import { cn } from '../../../../lib/utils';

function numberFormat(col) {
  return {
    format: 'number',
    decimals: col.decimals != null ? Number(col.decimals) : 0,
    useGrouping: true,
  };
}

function percentText(percent, col) {
  if (percent == null || !Number.isFinite(Number(percent))) return '';
  const decimals = col.decimals != null ? Number(col.decimals) : 0;
  return formatReportValue(percent, { format: 'percent', decimals, useGrouping: false });
}

function Marker({ bar, size }) {
  if (!bar.hasGoal || bar.marker === 'none') return null;
  const inset = `${bar.fillPercent}%`;
  if (bar.marker === 'arrow') {
    return (
      <span
        className="absolute z-10 leading-none font-black pointer-events-none"
        style={{ right: `calc(${inset} - 0.4em)`, top: -1, color: bar.fill, fontSize: size.track + 4 }}
        aria-hidden
      >
        ▼
      </span>
    );
  }
  if (bar.marker === 'line') {
    return (
      <span
        className="absolute z-10 w-0.5 rounded-sm"
        style={{
          right: inset,
          top: -3,
          bottom: -3,
          backgroundColor: bar.fill,
        }}
      />
    );
  }
  return (
    <span
      className="absolute z-10 rounded-full border-2 border-white shadow"
      style={{
        right: `calc(${inset} - ${size.track / 2}px)`,
        top: '50%',
        width: size.track + 2,
        height: size.track + 2,
        marginTop: -((size.track + 2) / 2),
        backgroundColor: bar.fill,
      }}
    />
  );
}

function BarTrack({ bar, size }) {
  return (
    <div className="relative w-full" style={{ height: size.track + (bar.marker === 'arrow' ? 12 : 4) }}>
      {bar.marker === 'arrow' && <Marker bar={bar} size={size} />}
      <div
        className="absolute inset-x-0 bottom-0 rounded-full"
        dir="rtl"
        style={{
          height: size.track,
          backgroundColor: bar.track,
          boxShadow: bar.border ? `inset 0 0 0 1.5px ${bar.borderColor}` : undefined,
        }}
      >
        {bar.hasGoal && bar.fillPercent > 0 && (
          <div
            className="h-full rounded-full"
            style={{ width: `${bar.fillPercent}%`, backgroundColor: bar.fill }}
          />
        )}
        {bar.marker !== 'arrow' && <Marker bar={bar} size={size} />}
        {bar.showPercent && (
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center font-bold tabular-nums pointer-events-none',
              size.labelClass,
            )}
            style={{
              color: bar.fillPercent > 45 ? '#fff' : bar.fill,
              textShadow: bar.fillPercent > 45 ? '0 0 3px rgba(0,0,0,.35)' : undefined,
            }}
          >
            {percentText(bar.percent, { decimals: 0 })}
          </span>
        )}
      </div>
    </div>
  );
}

function GaugeMeter({ bar, size }) {
  const w = size.gauge;
  const h = Math.round(w * 0.62);
  const sw = 11;
  const filled = bar.hasGoal ? bar.fillPercent : 0;
  // Clockwise top semicircle (SVG Y grows down). pathLength keeps fill at 0–100.
  const d = 'M 12 52 A 38 38 0 0 1 88 52';
  return (
    <div className="relative mx-auto" style={{ width: w, height: h }}>
      <svg viewBox="0 0 100 62" width={w} height={h} className="overflow-visible">
        <path
          d={d}
          fill="none"
          stroke={bar.track}
          strokeWidth={sw}
          strokeLinecap="round"
          pathLength="100"
        />
        {bar.hasGoal && filled > 0 && (
          <path
            d={d}
            fill="none"
            stroke={bar.fill}
            strokeWidth={sw}
            strokeLinecap="round"
            pathLength="100"
            strokeDasharray={`${filled} 100`}
          />
        )}
        {bar.border && (
          <path d={d} fill="none" stroke={bar.borderColor} strokeWidth={1.25} pathLength="100" />
        )}
      </svg>
      {bar.showPercent && (
        <div
          className={cn('absolute inset-x-0 bottom-0 text-center font-bold tabular-nums', size.labelClass)}
          style={{ color: bar.fill }}
        >
          {percentText(bar.percent, { decimals: 0 })}
        </div>
      )}
    </div>
  );
}

function RingMeter({ bar, size }) {
  const w = size.gauge;
  const r = 36;
  const filled = bar.hasGoal ? bar.fillPercent : 0;
  return (
    <div className="relative mx-auto" style={{ width: w, height: w }}>
      <svg viewBox="0 0 100 100" width={w} height={w}>
        <circle cx="50" cy="50" r={r} fill="none" stroke={bar.track} strokeWidth={10} pathLength="100" />
        {bar.hasGoal && filled > 0 && (
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={bar.fill}
            strokeWidth={10}
            strokeLinecap="round"
            pathLength="100"
            strokeDasharray={`${filled} 100`}
            transform="rotate(-90 50 50)"
          />
        )}
        {bar.border && (
          <circle cx="50" cy="50" r={r + 7} fill="none" stroke={bar.borderColor} strokeWidth={1.25} />
        )}
      </svg>
      {bar.showPercent && (
        <span
          className={cn('absolute inset-0 flex items-center justify-center font-bold tabular-nums', size.labelClass)}
          style={{ color: bar.fill }}
        >
          {percentText(bar.percent, { decimals: 0 })}
        </span>
      )}
    </div>
  );
}

function BadgeMeter({ bar, size }) {
  const text = bar.hasGoal ? percentText(bar.percent, { decimals: 0 }) : '—';
  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-full px-2 py-0.5 font-bold tabular-nums', size.labelClass)}
      style={{
        backgroundColor: bar.track === 'rgba(15, 23, 42, 0.08)' ? `${bar.fill}22` : bar.track,
        color: bar.fill,
        boxShadow: bar.border ? `inset 0 0 0 1.5px ${bar.borderColor}` : undefined,
        minWidth: size.gauge * 0.55,
      }}
    >
      {text}
    </span>
  );
}

export default function MeterCell({ col, value, row, tableLook }) {
  const bar = resolveBarDisplay(row, col, value, tableLook);
  const size = bar.size;
  const fmt = numberFormat(col);
  const startText = formatReportValue(bar.startRaw != null ? bar.startRaw : bar.start, fmt);
  const endText = bar.hasGoal
    ? formatReportValue(bar.endRaw != null ? bar.endRaw : bar.end, fmt)
    : '';
  const kind = col.display || 'bar';

  let visual = null;
  if (kind === 'gauge') visual = <GaugeMeter bar={bar} size={size} />;
  else if (kind === 'ring') visual = <RingMeter bar={bar} size={size} />;
  else if (kind === 'badge') visual = <BadgeMeter bar={bar} size={size} />;
  else visual = <BarTrack bar={bar} size={size} />;

  const showEnds = bar.showLabels && kind !== 'badge';

  return (
    <div className="flex flex-col gap-0.5 w-full min-w-0">
      {visual}
      {showEnds && (
        <div className={cn('flex items-center justify-between gap-1 tabular-nums text-slate-600 leading-tight', size.labelClass)}>
          <span className="truncate">{startText}</span>
          <span className="truncate">{endText}</span>
        </div>
      )}
    </div>
  );
}

export function rowMeterStyle(meter) {
  if (!meter?.fillRow || !meter?.hasGoal) return undefined;
  const pct = meter.fillPercent ?? 0;
  return {
    backgroundImage: `linear-gradient(to left, ${meter.fill}33 ${pct}%, transparent ${pct}%)`,
  };
}
